import { APP_URL } from "@/src/core/config/app";
import { PLACE_HOLDER_IMAGE } from "@/src/shared/constants/app";
import { FEATURED_TITLE } from "@/src/shared/features/boost";
import type { ICampaignListing, IListing } from "@/src/shared/types/listing";
import type {
  IHomepageRail,
  IHomepageRailListing,
  IHomepageTab,
} from "./types";

type ITabLink = Pick<IHomepageTab, "id" | "title">;

/** "Rooms under ৳2,000" → "rooms-under-2000"; empty when nothing Latin or numeric is left. */
const slugify = (text: string) =>
  text
    .normalize("NFKD")
    .toLowerCase()
    .replace(/(\d),(?=\d)/g, "$1")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Search engines show about 160 characters of a description; under ~120 reads as thin. */
const DESCRIPTION_MAX = 160;

/**
 * A rail page's meta description, 120–160 characters: the rail's subtitle (or an opening
 * line), then what the visitor gets. The longest wording that fits wins.
 */
const railDescription = (
  title: string,
  subtitle: string | null,
  kind: string,
): string => {
  const lead = subtitle
    ? `${subtitle.trim().replace(/[.!]+$/, "")}.`
    : `Discover ${title.toLowerCase()} for your next trip.`;
  const shortest = `${lead} Book verified ${kind} across Bangladesh on Travela.`;
  const wordings = [
    `${lead} Browse verified ${kind} across Bangladesh with real photos, honest guest reviews and secure, easy booking on Travela.`,
    `${lead} Browse verified ${kind} across Bangladesh with real photos, guest reviews and easy booking.`,
    shortest,
  ];
  const fits = wordings.find((text) => text.length <= DESCRIPTION_MAX);
  // A very long subtitle: cut, so the snippet still ends cleanly.
  return fits ?? `${shortest.slice(0, DESCRIPTION_MAX - 1).trimEnd()}…`;
};

/** What a rail page (`/stays/{slug}`) shows: a tab's rail, or that tab's Featured Stays. */
export type TRailMatch =
  | { kind: "rail"; tab: IHomepageTab; rail: IHomepageRail }
  | { kind: "featured"; tab: IHomepageTab };

const hasTab = (tabs: IHomepageTab[], slug?: string | null): slug is string =>
  !!slug && tabs.some((tab) => tab.slug === slug);

export const homepageService = {
  /** A tab's own page; same `/{Title}-{id}` shape the campaign links used, so old URLs keep a home. */
  tabHref(tab: ITabLink): string {
    return `/${tab.title}-${tab.id}`;
  },

  /** Title/description/canonical for a tab's page. */
  buildTabSeo(tab: ITabLink) {
    const tabType = tab.title || "Properties";
    return {
      title: `${tabType} for Rent | Affordable & Verified | Travela`,
      description: `Find affordable, verified ${tabType.toLowerCase()} for rent in Bangladesh. Book now with Travela!`,
      canonical: `${APP_URL}${homepageService.tabHref(tab)}`,
    };
  },

  /** The tab to show: the visitor's pick, else the default tab, else the first; null if none exist. */
  resolveSelectedSlug(
    tabs: IHomepageTab[],
    pickedSlug: string | null,
    defaultTab: string | null | undefined,
  ): string | null {
    if (hasTab(tabs, pickedSlug)) return pickedSlug;
    if (hasTab(tabs, defaultTab)) return defaultTab;
    return tabs[0]?.slug ?? null;
  },

  /** Old campaign links share the tab URL shape but their ids collide, so the title decides first. */
  findTabForPath(tabs: IHomepageTab[], path: string): IHomepageTab | null {
    const match = path.match(/^(.+)-(\d+)$/);
    if (!match) return null;
    const [, title = "", id] = match;
    const name = title.toLowerCase();
    return (
      tabs.find(
        (t) => t.title.toLowerCase() === name || t.slug.toLowerCase() === name,
      ) ??
      tabs.find((t) => t.id === Number(id)) ??
      null
    );
  },

  /** A rail's page slug, from its title (Airbnb-style readable URLs). */
  railSlug(rail: Pick<IHomepageRail, "id" | "title">): string {
    return slugify(rail.title) || `rail-${rail.id}`;
  },

  /** A homepage rail's click source, the `from_page` its cards' listing visits report. */
  railFromPage(
    tab: Pick<IHomepageTab, "slug">,
    rail: Pick<IHomepageRail, "id">,
  ): string {
    return `homepage_${tab.slug}_${rail.id}`;
  },

  /** Each tab has its own Featured Stays row, so its slug names the tab. */
  featuredSlug(tab: Pick<IHomepageTab, "slug">): string {
    return `featured-stays-${slugify(tab.slug)}`;
  },

  railHref(slug: string): string {
    return `/stays/${slug}`;
  },

  /** The rail a `/stays/{slug}` page is for, searching the tabs in display order. */
  findRail(
    tabsWithRails: { tab: IHomepageTab; rails: IHomepageRail[] }[],
    slug: string,
  ): TRailMatch | null {
    for (const { tab, rails } of tabsWithRails) {
      if (homepageService.featuredSlug(tab) === slug) {
        return { kind: "featured", tab };
      }
      const rail = rails.find((r) => homepageService.railSlug(r) === slug);
      if (rail) return { kind: "rail", tab, rail };
    }
    return null;
  },

  /** A matched rail's slug, title and subtitle (Featured Stays has no subtitle on the wire). */
  matchDetails(match: TRailMatch): {
    slug: string;
    title: string;
    subtitle: string | null;
  } {
    if (match.kind === "featured") {
      return {
        slug: homepageService.featuredSlug(match.tab),
        title: FEATURED_TITLE,
        subtitle: null,
      };
    }
    return {
      slug: homepageService.railSlug(match.rail),
      title: match.rail.title,
      subtitle: match.rail.subtitle,
    };
  },

  /** Title, description, keywords and canonical for a rail's page. */
  buildRailSeo(rail: {
    slug: string;
    title: string;
    subtitle: string | null;
    tabTitle: string;
  }) {
    const kind = rail.tabTitle.toLowerCase() || "stays";
    return {
      title: `${rail.title} | ${rail.tabTitle} in Bangladesh | Travela`,
      description: railDescription(rail.title, rail.subtitle, kind),
      keywords: [
        rail.title,
        `${rail.tabTitle} in Bangladesh`,
        `${kind} for rent`,
        `book ${kind} online`,
        "Travela",
      ],
      canonical: `${APP_URL}${homepageService.railHref(rail.slug)}`,
    };
  },

  /** Structured data for a rail's page: its breadcrumb and the stays it lists. */
  buildRailJsonLd(
    rail: { slug: string; title: string },
    tab: ITabLink,
    stays: { id: number; isHotel: boolean }[],
  ): Record<string, unknown>[] {
    const pageUrl = `${APP_URL}${homepageService.railHref(rail.slug)}`;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: APP_URL },
          {
            "@type": "ListItem",
            position: 2,
            name: tab.title,
            item: `${APP_URL}${homepageService.tabHref(tab)}`,
          },
          { "@type": "ListItem", position: 3, name: rail.title, item: pageUrl },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: rail.title,
        url: pageUrl,
        itemListElement: stays.map((stay, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${APP_URL}/${stay.isHotel ? "hotel" : "listing"}/${stay.id}`,
        })),
      },
    ];
  },

  /** Rail cards are a slim projection; widen them so they render through the shared ListingCard. */
  railCardToListing(card: IHomepageRailListing): ICampaignListing {
    return {
      listing: {
        id: card.listing_id,
        title: card.title ?? "",
        price: card.price ?? 0,
        reviews_avg: card.avg_review ?? 0,
        // No count on the wire: a rated card has at least one review, an unrated one shows "New".
        reviews_count: card.avg_review ? 1 : 0,
        is_hotel: card.is_hotel_portfolio,
        images: [
          {
            id: card.listing_id,
            url: card.image_url || PLACE_HOLDER_IMAGE,
            priority: 0,
          },
        ],
      } as unknown as IListing,
    } as ICampaignListing;
  },
};
